/**
 * THE ASSIGNMENT MODEL — pure. No React, no JSON import, no API, no I/O.
 *
 * This is the adapter between a normalized Command Center record and the staged
 * scoring logic in `lib/assignmentEngine.js`. Every function takes the routing
 * map as an argument rather than importing it, which is what lets
 * `verify-assignments.mjs` assert the real behaviour under plain Node.
 *
 * `assignments.js` is the bound layer: it loads `data/rep_proficiency.json`,
 * partially applies these, and adds the things that need the browser (the audit
 * trail read).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * GOVERNANCE — propose, don't overwrite.
 *
 *  · `proposalFor()` returns NULL for a record that already has an owner, so an
 *    owned opportunity is never re-proposed and can never be auto-corrected
 *    toward a "better" rep. Returning null rather than a suppressed proposal is
 *    deliberate: a caller cannot accidentally draw a suggestion over somebody's
 *    assignment.
 *  · Nothing here writes. A HUMAN holding "Manage Assignments" is the only
 *    thing that does, and a human MAY override an existing owner — that is what
 *    a reassignment is.
 *  · The write field is `sr_assigned_rep` (a DISPLAY-NAME string), never
 *    `hubspot_owner_id`: only 2 of the 15 reps are HubSpot owners.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { scoreOpportunity } from '../../lib/assignmentEngine.js'
import { ownerName, isRetired } from '../../lib/championships.js'

/** The HubSpot property a reassignment writes. Never `hubspot_owner_id`. */
export const ASSIGN_FIELD = 'sr_assigned_rep'

/** The audit entity every assignment change is filed under. */
export const ASSIGN_ENTITY = 'championship-assignment'

/** The audit action name — one string, so the trail is filterable. */
export const ASSIGN_ACTION = 'cc.rep.assign'

/* ---- who needs a rep ------------------------------------------------- */

/**
 * Does this record still need an owner?
 *
 * `ownerName()` is the platform's one answer to "is anybody on this?" — it
 * already coerces owner objects and treats Unassigned / unknown / none / blank
 * as nobody, so this never re-derives the rule.
 */
export function needsAssignment(record) {
  /* ⚠️ A RETIRED RECORD CAN NEVER BE CROWNED, SO IT NEEDS NOBODY.
   *
   * Readiness reports "33 RETIRED — CANNOT BE CROWNED"; the Assignments
   * worklist and Gaps were proposing reps onto exactly those records
   * ("RETIRED - Atlantic Coast Conference Men's Water Polo Championship —
   * Suggested rep: Philip Davanzo III"). Putting a person's name against work
   * that cannot exist is worse than leaving it blank: it manufactures a task.
   *
   * A human can still reassign one by hand — `proposalForReassign` is
   * deliberately untouched — because correcting a record's owner is a
   * different act from the engine surfacing it as work. */
  if (record?.retired || isRetired(record)) return false
  return !ownerName(record)
}

/** The same question, stated as the governance rule: an OWNED record is off
 *  limits to the engine. */
export function needsProposal(record) {
  return needsAssignment(record)
}

/** A record's owner as the write sees it — '' when nobody is on it. The `from`
 *  half of every audit entry. */
export function currentRep(record) {
  return ownerName(record)
}

/* ---- record → opportunity -------------------------------------------- */

/**
 * Adapt a normalized Command Center record into the shape the engine scores.
 *
 * Two honest translations:
 *  · `NATIONAL` is this platform's word for "no state on the record", not a
 *    place, so it is passed as blank rather than matched against `states_won`.
 *  · A championship record carries no HS governing body today, so the field is
 *    passed only when one actually exists. The engine simply scores no
 *    governing-body signal — it never invents one.
 */
export function toOpportunity(record) {
  if (!record) return null
  const state = record.state && record.state !== 'NATIONAL' ? record.state : ''
  return {
    name: record.name || '',
    sport: record.sport || '',
    state,
    // `segment` is how the org categorizes it (HS State / College / All-Star),
    // which is the better level signal when `sr_level` is blank.
    level: record.level || record.segment || '',
    conference: record.conference || '',
    governing_body: record.governingBody || record.governing_body || '',
  }
}

/** The proposal for one record, or NULL when it already has an owner. */
export function proposalFor(record, map) {
  if (!record || !needsProposal(record)) return null
  return scoreOpportunity(toOpportunity(record), map)
}

/**
 * The proposal for a record REGARDLESS of ownership — for the reassignment
 * picker, where a human has explicitly asked "who else could take this?".
 * Never used for passive surfacing.
 */
export function proposalForReassign(record, map) {
  if (!record) return null
  return scoreOpportunity(toOpportunity(record), map)
}

/* ---- confidence ------------------------------------------------------ */

export const CONFIDENCE_META = {
  high: { label: 'High confidence', color: '#2f7d4f' },
  medium: { label: 'Medium confidence', color: '#a5762a' },
  low: { label: 'Low confidence', color: '#8a6a3a' },
  none: { label: 'No match', color: '#9a3b3b' },
}

export function confidenceMeta(confidence) {
  return CONFIDENCE_META[confidence] || CONFIDENCE_META.none
}

/* ---- the audit pair --------------------------------------------------- */

/**
 * Compose the audit context for one reassignment. Passed straight to
 * `api.assignRep`, which hands it to `request()` so the write observer files a
 * real before/after pair instead of intent alone.
 */
export function assignmentAudit(record, to) {
  return {
    entity: ASSIGN_ENTITY,
    entityId: record?.id ?? null,
    action: ASSIGN_ACTION,
    before: { [ASSIGN_FIELD]: currentRep(record) || null, name: record?.name || '' },
    after: { [ASSIGN_FIELD]: to, name: record?.name || '' },
  }
}

/**
 * The proven reps from a routing map, in descending proven volume. This is the
 * SUGGESTION side of the split below — it is what the engine scores against.
 */
export function repsFrom(map) {
  const reps = (map && map.reps) || {}
  return Object.keys(reps)
    .map((key) => {
      const r = reps[key] || {}
      return {
        key,
        name: r.display_name || key,
        wins: r.total_wins || 0,
        hubspotOwnerId: r.hubspot_owner_id || null,
        isCheerRep: !!r.cheer_allstar?.is_cheer_rep,
      }
    })
    .sort((a, b) => b.wins - a.wins || a.name.localeCompare(b.name))
}

/* ---- the picker's roster --------------------------------------------- */

/**
 * THE SPLIT: the engine SUGGESTS from the win history (15 proven reps), the
 * human PICKS from the whole company.
 *
 * A record isn't only ever a rep's job — artwork goes to a designer, a
 * partnership to the cheer team, a production question to manufacturing. So the
 * picker's options are the full SR team roster (`data/sr_team_roster.json`),
 * while `repsFrom()` above — the scoring side — is untouched. Neither knows
 * about the other, which is why expanding who can be assigned cannot leak into
 * who gets suggested.
 *
 * Still a display NAME, still written to `sr_assigned_rep`: a designer is not a
 * HubSpot owner either, so nothing changes about the write.
 */
export function teamFrom(roster) {
  const team = (roster && roster.team) || []
  return team
    .filter((m) => m && m.assignable !== false && typeof m.name === 'string' && m.name.trim())
    .map((m) => {
      const category = String(m.category || '').trim()
      // "Sales/Pursuit Rep (Class rings/Grad)" — the group is the part before
      // the parenthetical, the parenthetical is that person's specific role.
      const detail = /\(([^)]+)\)\s*$/.exec(category)
      return {
        id: m.clickup_id ?? m.name,
        name: m.name.trim(),
        email: m.email || '',
        category,
        role: detail ? detail[1].trim() : '',
        clickupId: m.clickup_id ?? null,
      }
    })
}

/**
 * The same roster, bucketed into the groups `meta.groups_order` declares — the
 * shape an `<optgroup>` dropdown renders straight off.
 *
 * A category the order doesn't name is NOT dropped: it lands in a trailing
 * "Other" group, because a team member missing from the picker is a person
 * nobody can assign work to. Empty groups are omitted, so a roster that loses a
 * department doesn't leave a blank heading behind.
 */
export function groupedTeamFrom(roster) {
  const order = (roster?.meta?.groups_order || []).filter(Boolean)
  const buckets = new Map(order.map((g) => [g, []]))
  const other = []

  for (const m of teamFrom(roster)) {
    // Longest match wins, so a group name that is a prefix of another can never
    // swallow its sibling.
    let best = ''
    for (const g of order) {
      if ((m.category === g || m.category.startsWith(`${g} (`)) && g.length > best.length) best = g
    }
    if (best) buckets.get(best).push(m)
    else other.push(m)
  }

  const groups = order
    .map((g) => ({ group: g, members: buckets.get(g) }))
    .filter((g) => g.members.length > 0)
  if (other.length) groups.push({ group: 'Other', members: other })
  return groups
}
